import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, LogOut, Loader2, Camera, Wand2, LayoutDashboard, KeyRound, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function ProfilePanel() {
  const { t } = useTranslation("portal");
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [removeBackground, setRemoveBackground] = useState(false);
  const [form, setForm] = useState({
    given_name: "",
    surname: "",
    phone: "",
    email: "",
  });

  // Password management
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Load profile when panel opens
  useEffect(() => {
    if (!user || !open) return;
    supabase
      .from("profiles")
      .select("given_name, surname, phone, email, profile_photo_url")
      .eq("id", user.id)
      .single()
      .then(async ({ data }) => {
        const email = data?.email || user.email || "";
        setForm({
          given_name: data?.given_name || "",
          surname: data?.surname || "",
          phone: data?.phone || "",
          email,
        });

        // Load photo from R2 if we have a stored key
        if (data?.profile_photo_url) {
          loadPhotoFromR2(data.profile_photo_url);
        }

        // Check if user has a password set
        if (email) {
          supabase.functions.invoke("check-auth-method", { body: { email } })
            .then(({ data: authData }) => {
              if (authData !== null) setHasPassword(authData.hasPassword ?? false);
            })
            .catch(() => { /* silent — password section stays hidden */ });
        }
      });
  }, [user, open]);

  const loadPhotoFromR2 = async (r2Key: string) => {
    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;
    if (!token) return;
    try {
      const res = await fetch(`/api/files/${r2Key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        setPhotoUrl(URL.createObjectURL(blob));
      }
    } catch { /* silent */ }
  };

  const handlePhotoUpload = async (file: File) => {
    if (!user) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ variant: "destructive", title: "File too large", description: "Max 10MB." });
      return;
    }

    setPhotoProcessing(true);

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPhotoUrl(e.target?.result as string);
    reader.readAsDataURL(file);

    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;

    // 1. Upload original to R2
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", "profile");

    const uploadRes = await fetch("/api/upload", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });

    if (!uploadRes.ok) {
      toast({ variant: "destructive", title: "Upload failed" });
      setPhotoProcessing(false);
      return;
    }

    const { key: originalKey } = await uploadRes.json();

    let finalKey = originalKey;
    let wasProcessed = false;

    // 2. AI background removal — only if user opted in
    if (removeBackground) {
      toast({ title: "✨ Processing photo…", description: "Removing background with AI." });

      try {
        const processRes = await fetch("/api/process-photo", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ r2Key: originalKey }),
        });

        if (processRes.ok) {
          const result = await processRes.json();
          finalKey = result.key;
          wasProcessed = result.processed ?? true;
        }
        // If not ok: silently fall back to original (don't expose error to user)
      } catch {
        // Network/processing error — use original photo silently
      }
    }

    // 3. Update profiles table with the final key
    await supabase.from("profiles").update({ profile_photo_url: finalKey }).eq("id", user.id);

    // 4. Load processed photo from R2 for display
    await loadPhotoFromR2(finalKey);

    setPhotoProcessing(false);
    toast({
      title: "✅ Photo saved",
      description: wasProcessed ? "Background removed successfully." : "Your profile photo has been updated.",
    });
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ given_name: form.given_name, surname: form.surname, phone: form.phone })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      toast({ variant: "destructive", title: "Failed to save", description: error.message });
    } else {
      toast({ title: "Profile saved ✓" });
      setOpen(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
  };

  if (!user) return null;

  const initials = [form.given_name, form.surname]
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .join("") || user.email?.[0]?.toUpperCase() || "?";

  // ── Avatar component (used in trigger + panel) ─────────────────────────────
  const AvatarCircle = ({ size = "sm" }: { size?: "sm" | "lg" }) => {
    const cls = size === "lg"
      ? "w-20 h-20 text-2xl ring-4"
      : "w-9 h-9 text-sm ring-2";
    return (
      <div className={`relative rounded-full overflow-hidden ${cls} ring-primary/20 bg-primary text-primary-foreground flex items-center justify-center font-semibold`}>
        {photoUrl ? (
          <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
        ) : initials !== "?" ? (
          <span>{initials}</span>
        ) : (
          <UserCircle className={size === "lg" ? "h-10 w-10" : "h-5 w-5"} />
        )}
        {photoProcessing && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-white" />
          </div>
        )}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="hover:opacity-90 transition-opacity"
          aria-label="Edit profile"
          title="My profile"
        >
          <AvatarCircle size="sm" />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-sm flex flex-col">
        <SheetHeader className="pb-2">
          <SheetTitle className="font-heading">{t("common.memberPortal")}</SheetTitle>
        </SheetHeader>

        {/* My Portal shortcut */}
        <button
          onClick={() => { navigate("/portal"); setOpen(false); }}
          className="flex items-center gap-3 w-full rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 px-4 py-3 transition-colors group"
        >
          <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0 group-hover:bg-primary/25 transition-colors">
            <LayoutDashboard className="h-4 w-4 text-primary" />
          </div>
          <div className="text-left flex-1">
            <p className="text-sm font-semibold text-primary">{t("common.memberPortal")}</p>
            <p className="text-xs text-muted-foreground">{t("dashboard.myApplications")}</p>
          </div>
          <span className="text-primary/40 group-hover:text-primary transition-colors text-xs">→</span>
        </button>

        {/* Avatar — clickable to upload */}
        <div className="flex flex-col items-center gap-2 py-4">
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0])}
          />
          <button
            onClick={() => photoInputRef.current?.click()}
            disabled={photoProcessing}
            className="relative group"
            title="Change profile photo"
          >
            <AvatarCircle size="lg" />
            {/* Camera overlay on hover */}
            <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
          {/* Background removal toggle */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-lg px-3 py-2 bg-muted/40">
            <Wand2 className="h-3.5 w-3.5 text-accent flex-shrink-0" />
            <span className="flex-1">Remove background with AI</span>
            <Switch
              checked={removeBackground}
              onCheckedChange={setRemoveBackground}
              className="scale-75"
              aria-label="Remove background with AI"
            />
          </div>
          {removeBackground && (
            <p className="text-xs text-muted-foreground text-center">
              Background will be replaced with white — ideal for passport-style photos.
            </p>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto">
          <div className="space-y-2">
            <Label htmlFor="given_name">{t("personalInfo.identity.givenName")}</Label>
            <Input id="given_name" value={form.given_name}
              onChange={(e) => setForm((f) => ({ ...f, given_name: e.target.value }))} placeholder="Jean" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="surname">{t("personalInfo.identity.surname")}</Label>
            <Input id="surname" value={form.surname}
              onChange={(e) => setForm((f) => ({ ...f, surname: e.target.value }))} placeholder="Dupont" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{t("personalInfo.contact.phone")}</Label>
            <Input id="phone" value={form.phone} type="tel"
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+66 80 000 0000" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("personalInfo.contact.email")}</Label>
            <Input id="email" value={form.email} disabled className="bg-muted/50 cursor-not-allowed" />
            <p className="text-xs text-muted-foreground">Email cannot be changed here.</p>
          </div>

          {/* Password management — shown once check-auth-method responds */}
          {hasPassword !== null && (
            <div className="border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  {hasPassword ? "Password" : "No password set"}
                </span>
              </div>

              {!showPasswordForm ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={async () => {
                    if (hasPassword) {
                      // Send reset email
                      setSavingPassword(true);
                      const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
                        redirectTo: window.location.origin + "/portal/auth/callback",
                      });
                      setSavingPassword(false);
                      if (error) {
                        toast({ variant: "destructive", title: "Error", description: error.message });
                      } else {
                        toast({ title: "Reset link sent", description: "Check your email to change your password." });
                      }
                    } else {
                      setShowPasswordForm(true);
                    }
                  }}
                  disabled={savingPassword}
                >
                  {savingPassword
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Sending…</>
                    : hasPassword ? "Change password" : "Set a password"
                  }
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-xs">New password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPwd ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min. 8 characters"
                        className="pr-10"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                        onClick={() => setShowNewPwd((v) => !v)}
                        tabIndex={-1}
                      >
                        {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-xs">Confirm password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat password"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="flex-1"
                      disabled={savingPassword}
                      onClick={async () => {
                        if (newPassword.length < 8) {
                          toast({ variant: "destructive", title: "Password too short", description: "Minimum 8 characters." });
                          return;
                        }
                        if (newPassword !== confirmPassword) {
                          toast({ variant: "destructive", title: "Passwords don't match" });
                          return;
                        }
                        setSavingPassword(true);
                        const { error } = await supabase.auth.updateUser({ password: newPassword });
                        setSavingPassword(false);
                        if (error) {
                          toast({ variant: "destructive", title: "Error", description: error.message });
                        } else {
                          toast({ title: "Password saved ✓" });
                          setShowPasswordForm(false);
                          setNewPassword("");
                          setConfirmPassword("");
                          setHasPassword(true);
                        }
                      }}
                    >
                      {savingPassword ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Saving…</> : "Save password"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowPasswordForm(false); setNewPassword(""); setConfirmPassword(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pt-4 space-y-2 border-t border-border mt-4">
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("common.saving")}</> : t("common.saveAndContinue")}
          </Button>
          <Button variant="ghost" onClick={handleSignOut}
            className="w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10">
            <LogOut className="h-4 w-4 mr-2" />{t("common.signOut")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
